import currency from 'currency.js';
import { format, addDays, subMonths, parseISO, differenceInSeconds } from 'date-fns';
import { de } from 'date-fns/locale';

export const euro = (value, options) => {
    return currency(value, {
        symbol: '',
        separator: '.',
        decimal: ',',
        fromCents: true,
        ...options,
    });
};
export const round2d = (value) => {
    return currency(value, { symbol: '', separator: '.', decimal: ',', fromCents: false });
};

export const priceToEuroString = (price) => {
    return euro(price).format() + ' €';
};

export const formatCurrency = (value) => {
    if (value === null || value === undefined) return '--';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};

export const formatEuroShort = (cents) => {
    if (cents == null || isNaN(cents)) return '0 €';
    const value = cents / 100;
    const abs = Math.abs(value);
    const toComma = (n) => String(n);

    if (abs >= 1_000_000) {
        return `${toComma((value / 1_000_000).toFixed(1))}M €`;
    }
    if (abs >= 1_000) {
        return `${toComma((value / 1_000).toFixed(1))}K €`;
    }
    return priceToEuroString(cents);
};

export const distanceToString = (distance, unit, suffix = 'km') => {
    if (unit === 'km') {
        return `${round2d(distance).format().replace(',00', '')}${suffix}`;
    }
    return `${round2d(distance / 1000).format()}${suffix}`;
};

export const percentageToString = (percentage) => {
    return `${round2d(percentage * 100).format()} %`;
};

export const timeToString = (time, options = {}) => {
    const { withLabel } = options;

    const totalHours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);

    if (withLabel) {
        const parts = [];

        if (totalHours > 0) {
            parts.push(`${totalHours}h`);
        }
        if (minutes > 0 || parts.length === 0) {
            parts.push(`${minutes}m`);
        }

        return parts.join(' ');
    } else {
        const timePart = `${totalHours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}`;

        return timePart;
    }
};

export const getDuration = (seconds) => {
    return timeToString(seconds);
};

export const dateToString = (date) => {
    if (!date) {
        return '';
    }
    return format(new Date(date), 'dd.MM.yyyy');
};

export const formatDateTime = (dateString) => {
    if (!dateString) return '--';
    try {
        const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
        return format(date, 'dd.MM.yy HH:mm', { locale: de });
    } catch {
        return '--';
    }
};

export const formatAmount = (cents) => {
    if (cents === null || cents === undefined) return '--';
    return (cents / 100).toLocaleString('de-DE', {
        style: 'currency',
        currency: 'EUR',
    });
};

export const formatTimeDelta = (seconds) => {
    if (seconds === null || seconds === undefined) return '±0 min';
    const mins = Math.round(seconds / 60);
    if (mins === 0) return '±0 min';

    const absMins = Math.abs(mins);
    const sign = mins > 0 ? '-' : '+';

    if (absMins >= 60) {
        const hours = Math.floor(absMins / 60);
        const remainingMins = absMins % 60;
        return remainingMins > 0 ? `${sign}${hours}h ${remainingMins}min` : `${sign}${hours}h`;
    }

    return `${sign}${absMins} min`;
};

export const formatTimeDiff = (startDate, endDate) => {
    if (!startDate || !endDate) return '--';
    try {
        const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
        const end = typeof endDate === 'string' ? parseISO(endDate) : endDate;
        const diffSeconds = differenceInSeconds(end, start);
        const diffMinutes = Math.round(diffSeconds / 60);

        if (diffMinutes === 0) return '±0 min';

        const absMins = Math.abs(diffMinutes);
        const sign = diffMinutes > 0 ? '+' : '-';

        if (absMins >= 60) {
            const hours = Math.floor(absMins / 60);
            const mins = absMins % 60;
            return mins > 0 ? `${sign}${hours}h ${mins}min` : `${sign}${hours}h`;
        }

        return `${sign}${absMins} min`;
    } catch {
        return '--';
    }
};

export const formatAmountDelta = (cents) => {
    if (cents === null || cents === undefined) return '±0€';
    const euros = cents / 100;
    if (euros === 0) return '±0€';
    return euros > 0 ? `+${euros.toFixed(2)}€` : `${euros.toFixed(2)}€`;
};

export const formatTripDuration = (startAt, endAt) => {
    if (!startAt || !endAt) return '--';
    try {
        const start = typeof startAt === 'string' ? parseISO(startAt) : startAt;
        const end = typeof endAt === 'string' ? parseISO(endAt) : endAt;
        const seconds = Math.round((end - start) / 1000);
        return timeToString(seconds, { withLabel: true });
    } catch {
        return '--';
    }
};

export const parseMileage = (value) => {
    console.log('parseMileage input:', value, 'type:', typeof value);
    if (!value) return '';

    const numericValue = String(value).replace(/km$/i, '').replace(/\./g, '').trim();
    console.log('parseMileage output:', numericValue);
    return numericValue;
};

export const fillAllDaysInDateRange = (
    dateFrom,
    dateTo,
    dailyIncome = [],
    isPreviousMonth = false,
) => {
    const chartData = [];

    const incomeMap = new Map();
    dailyIncome.forEach((item) => {
        const dateKey = format(new Date(item.date), 'yyyy-MM-dd');
        incomeMap.set(dateKey, item.income);
    });

    let currentDate = new Date(dateFrom);

    while (currentDate <= dateTo) {
        const dateKey = format(currentDate, 'yyyy-MM-dd');
        let income = 0;

        if (isPreviousMonth) {
            const prevMonthDate = subMonths(currentDate, 1);
            const prevMonthKey = format(prevMonthDate, 'yyyy-MM-dd');
            income = incomeMap.get(prevMonthKey) || 0;
        } else {
            income = incomeMap.get(dateKey) || 0;
        }

        chartData.push({
            date: format(currentDate, 'dd.MM', { locale: de }),
            value: income,
            formattedValue: euro(income).format(),
            formatter: (value) => euro(value).format(),
            formattedDate: format(currentDate, 'dd MMM yyyy', { locale: de }),
            rawDate: format(currentDate, 'yyyy-MM-dd'),
        });

        currentDate = addDays(currentDate, 1);
    }

    return chartData;
};
